<script setup lang="ts">
import { ref, useId } from 'vue';

import { PhxsgStack as PhxsgCoreStack } from '@sg-core/src/View/Components/Atoms/Stack';

import { PhxsgButton } from '../../Atoms/Button';
import { PhxsgHeading } from '../../Atoms/Heading';
import { PhxsgPicture } from '../../Atoms/Picture';
import { PhxsgText } from '../../Atoms/Text';

import { useColumnReveal } from './use-column-reveal';

import type { CollectionsBlockItem } from './phxsg-collections-block.vue';

const THUMBNAIL_REAPPEAR_DELAY = 600;

const { items, title } = defineProps<{
  items: CollectionsBlockItem[];
  title?: string;
}>();

const id = useId();
const rootRef = ref<HTMLElement | null>(null);
const selectedIndex = ref<number | null>(null);
const visibleThumbnails = ref(items.map(() => true));
const hasInteracted = ref(false);

useColumnReveal(rootRef);

function selectColumn(index: number): void {
  hasInteracted.value = true;
  const previousIndex = selectedIndex.value;

  if (index === previousIndex) {
    return;
  }

  visibleThumbnails.value[index] = false;

  if (previousIndex !== null) {
    visibleThumbnails.value[previousIndex] = false;

    setTimeout(() => {
      visibleThumbnails.value[previousIndex] = true;
    }, THUMBNAIL_REAPPEAR_DELAY);
  }

  selectedIndex.value = index;
}
</script>

<template>
  <div ref="rootRef" class="phxsg-collections-block-desktop">
    <PhxsgHeading
      v-if="title"
      use="h2"
      :class="[
        'phxsg-collections-block-desktop__title',
        { 'phxsg-collections-block-desktop__title--hidden': selectedIndex !== null },
      ]"
    >
      {{ title }}
    </PhxsgHeading>

    <div
      v-for="(item, index) in items"
      :key="`${item.title}-${index}`"
      :class="[
        'phxsg-collections-block-desktop__column',
        { 'phxsg-collections-block-desktop__column--expanded': selectedIndex === index },
      ]"
    >
      <div class="phxsg-collections-block-desktop__content">
        <button
          type="button"
          class="phxsg-collections-block-desktop__toggle"
          :aria-expanded="selectedIndex === index"
          :aria-controls="selectedIndex === index ? `${id}-details-${index}` : undefined"
          @click="selectColumn(index)"
        >
          {{ item.title }}
        </button>

        <Transition name="phxsg-collections-block-fade-content" mode="out-in">
          <PhxsgCoreStack
            v-if="selectedIndex === index"
            :id="`${id}-details-${index}`"
            spacing="4"
            class="phxsg-collections-block-desktop__details"
          >
            <PhxsgText v-if="item.text" use="p">
              {{ item.text }}
            </PhxsgText>

            <PhxsgButton v-if="item.cta" use="a" :href="item.cta.href" variant="fade">
              {{ item.cta.label }}
            </PhxsgButton>

            <PhxsgPicture
              v-if="item.picture"
              v-bind="item.picture"
              class="phxsg-collections-block-desktop__secondary"
            />
          </PhxsgCoreStack>
        </Transition>

        <Transition name="phxsg-collections-block-fade-image" mode="out-in">
          <div v-if="selectedIndex === index" class="phxsg-collections-block-desktop__big-image-wrapper">
            <PhxsgPicture
              v-bind="item.bigPicture"
              type="contain"
              class="phxsg-collections-block-desktop__image phxsg-picture--bottom"
            />
          </div>
        </Transition>

        <Transition name="phxsg-collections-block-fade-thumb" mode="out-in">
          <div v-if="visibleThumbnails[index] && selectedIndex !== index">
            <PhxsgPicture
              v-bind="item.thumbnail"
              :class="['phxsg-collections-block-desktop__image', { 'phxsg-fade-in-up': !hasInteracted }]"
            />
          </div>
        </Transition>

        <div class="phxsg-collections-block-desktop__thumbnail-info">
          <PhxsgHeading use="h4">{{ item.title }}</PhxsgHeading>
        </div>
      </div>
    </div>
  </div>
</template>
